/**
 * Writing a message.
 *
 * The surface is a card in the same slot the reader uses, so the column never
 * changes width and nothing shifts. There is no From field: the address comes
 * from the account, and postrd refuses a request whose sealing key does not
 * hold the address it names.
 *
 * It is built once and kept. A background sync repaints the counts around it
 * and never rebuilds this node, because rebuilding it would take the caret out
 * of the sentence somebody is typing.
 */
import { el, clear } from "./render";
import { icon } from "./icons";
import type { Draft } from "./draft";
import { whyNotSendable } from "./draft";
import type { BlobRef } from "../nostr/seal";

export interface ComposeOptions {
  draft: Draft;
  /** The address this message goes out as. Empty when it is not known yet. */
  account: string;
  /** Saves what has been written. Called as it is typed, and before it closes. */
  onSave: (draft: Draft) => Promise<void>;
  /** Leaves the composer. What was written is kept. */
  onClose: () => void;
  /** Throws away the draft and leaves. */
  onDiscard: () => Promise<void>;
  /** Sends. A note keeps a partially sent DM open for a safe retry. */
  onSend: (draft: Draft) => Promise<void>;
  /** Why this draft is back, when a send that was already closed failed. */
  notice?: string;
}

export interface Composer {
  node: HTMLElement;
  /** Writes what is on screen, now. */
  flush: () => Promise<void>;
  release: () => void;
}

const TITLES: Record<Draft["kind"], string> = {
  new: "New message",
  reply: "Reply",
  replyAll: "Reply to all",
  forward: "Forward",
};

/** Long enough that a keystroke is not a write, short enough to lose nothing. */
const SAVE_DELAY_MS = 500;

export function composer(opts: ComposeOptions): Composer {
  const draft = { ...opts.draft };
  let timer: ReturnType<typeof setTimeout> | null = null;
  let sending = false;
  const deliveryLocked = draft.via === "nostr" && !!draft.dmDelivery;
  /**
   * Set the moment the draft is thrown away or sent.
   *
   * Clicking Discard blurs the field first, and that blur writes. Without this
   * the write would land after the delete and bring the draft back.
   */
  let done = false;

  const status = el("div", { class: opts.notice ? "compose-status is-wrong" : "compose-status" }, opts.notice ?? "");

  async function save(): Promise<void> {
    if (timer) clearTimeout(timer);
    timer = null;
    if (done) return;
    draft.updated = Date.now();
    await opts.onSave(draft);
  }

  function saveSoon(): void {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => void save(), SAVE_DELAY_MS);
  }

  // A tab closed by accident must not take the message with it. The debounced
  // write is flushed here, which is the last moment the page is still alive.
  const onHide = (): void => void save();
  window.addEventListener("pagehide", onHide);

  function field(
    label: string,
    key: "to" | "cc" | "subject",
    type: string,
    placeholder: string,
  ): HTMLElement {
    const input = el("input", { class: "compose-input", type, placeholder, id: `compose-${key}` });
    input.value = draft[key];
    input.disabled = deliveryLocked;
    input.addEventListener("input", () => {
      draft[key] = input.value;
      saveSoon();
    });
    input.addEventListener("blur", () => void save());
    return el(
      "div",
      { class: "compose-row" },
      el("label", { class: "compose-label", for: `compose-${key}` }, label),
      input,
    );
  }

  /** A DM's recipients are keys, not text, so they are shown and not edited. */
  function fixed(label: string, value: string): HTMLElement {
    return el("div", { class: "compose-row" },
      el("span", { class: "compose-label" }, label),
      el("span", { class: "compose-input" }, value));
  }

  const body = el("textarea", {
    class: "compose-body", id: "compose-body", "aria-label": "Message",
    placeholder: "Write the message…",
  });
  body.value = draft.body;
  body.readOnly = deliveryLocked;
  body.addEventListener("input", () => {
    draft.body = body.value;
    saveSoon();
  });
  body.addEventListener("blur", () => void save());

  const close = el("button", { class: "back", type: "button" }, "← Back");
  close.addEventListener("click", () => {
    void save().then(opts.onClose);
  });

  const send = el("button", { class: "primary send", type: "button" }, deliveryLocked ? "Retry" : "Send");
  send.addEventListener("click", () => {
    // One press is one signature. A second press while the first is in flight
    // would ask the phone again and could put the same message on the relay
    // twice, and nobody can recall a message that did arrive.
    if (sending) return;
    const why = whyNotSendable(draft, opts.account);
    if (why) {
      status.textContent = why;
      status.className = "compose-status is-wrong";
      return;
    }
    sending = true;
    send.disabled = true;
    discard.disabled = true;
    send.textContent = "Sending…";
    status.className = "compose-status";
    status.textContent = "Sealing the message…";
    // The draft goes to the shell as it stands, and the composer is gone in
    // the same frame; the shell writes it to disk before anything leaves.
    done = true;
    if (timer) clearTimeout(timer);
    opts.onSend({ ...draft }).catch((err: unknown) => {
      done = false;
      sending = false;
      send.disabled = false;
      discard.disabled = false;
      send.textContent = "Send";
      status.className = "compose-status is-wrong";
      status.textContent = `Not sent: ${message(err)}. What you wrote is kept.`;
    });
  });

  const discard = el("button", { class: "secondary", type: "button" }, "Discard");
  discard.addEventListener("click", () => {
    done = true;
    if (timer) clearTimeout(timer);
    void opts.onDiscard();
  });

  const overNostr = draft.via === "nostr";
  const from = opts.account
    ? el("div", { class: "compose-from" }, el("span", {}, "From"), el("b", {}, opts.account),
      overNostr ? el("span", {}, "over Nostr") : "")
    : el("div", { class: "compose-from is-wrong" }, el("span", {},
      "This client does not know your address yet, so it cannot send."));

  const node = el(
    "section",
    { class: "detail compose", "aria-label": TITLES[draft.kind] },
    el("div", { class: "detail-head" }, close),
    el("h2", {}, TITLES[draft.kind]),
    from,
    overNostr ? fixed("To", draft.to) : field("To", "to", "email", "somebody@example.com"),
    overNostr ? "" : field("Cc", "cc", "email", ""),
    field("Subject", "subject", "text", ""),
    body,
    attachments(draft, () => saveSoon()),
    el("div", { class: "compose-actions" }, send, discard, status),
  );

  return {
    node,
    flush: save,
    release: () => {
      window.removeEventListener("pagehide", onHide);
      if (timer) clearTimeout(timer);
    },
  };
}

/**
 * The files a forward carries.
 *
 * They are references, not bytes: the blob store already holds them and postrd
 * reads it. Removing one here removes it from the message, not from the store.
 */
function attachments(draft: Draft, changed: () => void): HTMLElement {
  const slot = el("div", { class: "attachments" });
  function paint(): void {
    clear(slot);
    slot.hidden = (draft.attachments?.length ?? 0) === 0;
    for (const file of draft.attachments ?? []) slot.append(pill(file));
  }
  function pill(file: BlobRef): HTMLElement {
    const drop = el("button", {
      class: "attachment", type: "button",
      title: `Do not carry ${file.filename || "this file"}`,
    }, icon("paperclip", 16),
      el("span", { class: "name" }, file.filename || "(unnamed)"),
      icon("close", 14));
    drop.addEventListener("click", () => {
      draft.attachments = (draft.attachments ?? []).filter((f) => f.sha256 !== file.sha256);
      changed();
      paint();
    });
    return drop;
  }
  paint();
  return slot;
}

function message(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
